import api from "api/axios";

export const CONDUCTOR_ROLES = ["CEO", "HR", "Team Lead", "Technical Manager", "Other"] as const;
export type ConductorRole = (typeof CONDUCTOR_ROLES)[number];

export interface MeetingEmployeeOption {
  id: number;
  employee_code: string;
  name: string;
  department: string;
  designation: string;
  meeting_count: number;
  last_meeting_date: string | null;
}

export interface MeetingSummary {
  meeting_count: number;
  last_meeting_date: string | null;
  recent_count: number;
}

export interface Meeting {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  department: string;
  designation: string;
  meeting_date: string;
  conductor_role: ConductorRole;
  conductor_employee_id: number | null;
  conductor_employee_name: string | null;
  before_meeting_notes: string | null;
  after_meeting_remarks: string | null;
  follow_up: string | null;
  created_by: string;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface MeetingPayload {
  employee_id: number;
  meeting_date: string;
  conductor_role: ConductorRole;
  conductor_employee_id?: number | null;
  before_meeting_notes?: string | null;
  after_meeting_remarks?: string | null;
  follow_up?: string | null;
}

export interface MeetingListResult {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  summary: MeetingSummary;
  items: Meeting[];
}

export interface MeetingFilters {
  employee_id?: number;
  conductor_role?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

function meetingsApiBase(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/meetings" : "/employee/meetings";
}

export async function listMeetingEmployees(): Promise<MeetingEmployeeOption[]> {
  const res = await api.get(`${meetingsApiBase()}/employees`);
  return res.data;
}

export async function listMeetings(filters: MeetingFilters = {}): Promise<MeetingListResult> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  });
  const res = await api.get(`${meetingsApiBase()}?${params.toString()}`);
  return res.data;
}

export async function createMeeting(data: MeetingPayload): Promise<Meeting> {
  const res = await api.post(meetingsApiBase(), data);
  return res.data;
}

export async function updateMeeting(id: number, data: Partial<MeetingPayload>): Promise<Meeting> {
  const res = await api.patch(`${meetingsApiBase()}/${id}`, data);
  return res.data;
}

export async function deleteMeeting(id: number): Promise<void> {
  await api.delete(`/admin/meetings/${id}`); // delete stays admin-only on the backend
}
